import UserAvatar from "../ui/userAvatar";
import Send from "../../assets/svg/Send.svg";
import useCreateTaskMutation, {
  Comments,
  Task,
} from "@/api/mutation/useTaskCreateMutation";
import { useUser } from "@/hooks/useUser";
import calculateTimeDifference from "../shared/TimeDifferenceCalculate";
import { Button } from "../ui/button";
import { useFormik } from "formik";
import { createCommentTaskSchema } from "@backend/src/schemas/taskSchema";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import useCreateTaskCommentMutation, {
  Comment,
} from "@/api/mutation/useTaskAddCommentMutation";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import useUpdateTaskCommentMutation from "@/api/mutation/useTaskUpdateCommentMutation";
import useRemoveTaskCommentMutation from "@/api/mutation/useTaskRemoveCommentMutation";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import { errorConfig } from "@/lib/toastError";
import { generateUniqueTaskName } from "../shared/taskNameGenrater";
import useAllTaskQuery from "@/api/query/useAllTaskQuery";
import useProjectQuery, { AssignedUsers } from "@/api/query/useProjectQuery";
import { useTranslation } from "react-i18next";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import ErrorMessage from "../common/ErrorMessage";
import Dialog from "../common/Dialog";
import TrashCan from "../../assets/svg/TrashCan.svg";

type Props = {
  task: Task | undefined;
  refetch: () => void;
  allowed: boolean;
  setTaskId: (id: string) => void;
  setTask: (task: Task) => void;
  projectId?: string;
};

function TaskComment(props: Props) {
  const { task, refetch } = props;
  const currantUser = useUser();
  const [commentId, setCommentId] = useState<string | null>();
  const [showAllShow, setShowAllShow] = useState<boolean>(false);

  const createTaskCommentMutation = useCreateTaskCommentMutation(task?.taskId);
  const updateTaskCommentMutation = useUpdateTaskCommentMutation(
    commentId ?? undefined
  );
  const { t } = useTranslation();
  const taskCreateMutation = useCreateTaskMutation(
    props.projectId,
    props.task?.taskId ? props.task?.taskId : ""
  );
  const projectDetailQuery = useProjectDetail(props.task?.projectId);
  const removeTaskCommentMutation = useRemoveTaskCommentMutation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRefUpdate = useRef<HTMLTextAreaElement | null>(null);

  const commentFormik = useFormik<z.infer<typeof createCommentTaskSchema>>({
    initialValues: {
      commentText: "",
      mantionEmail: [], // Initialize mantionEmail array
    },
    validationSchema: toFormikValidationSchema(createCommentTaskSchema),
    onSubmit: async (values) => {
      let currentTaskId = props.task?.taskId;
      // If no task exists, create one first
      if (!currentTaskId) {
        try {
          const project = projects?.data?.data?.data.find(
            (p) => p.projectId === props.projectId
          );
          const projectStartDate = project?.startDate
            ? new Date(project.startDate)
            : new Date();

          const data = {
            taskName: generateUniqueTaskName(
              allTask.data?.data.data ?? [],
              project?.projectName ?? ""
            ),
            taskDescription: "",
            startDate:
              projectStartDate >= new Date() ? projectStartDate : new Date(),
            duration: 1.0,
          };
          const res = await taskCreateMutation.mutateAsync(data);
          currentTaskId = res.data.data.taskId;
          props.setTaskId(currentTaskId);
          props.setTask(res.data.data);
          toast.success("Task created successfully!");
        } catch (error: any) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
          return; // Stop submission if task creation failed
        }
      }

      createTaskCommentMutation.mutate(
        { ...values, taskId: currentTaskId! }, // Use the resolved task ID
        {
          onSuccess(data) {
            refetch();
            commentFormik.resetForm();
            toast.success(data.data.message);
          },
          onError(error: any) {
            toast.error(
              error.response?.data.message == "Unauthorized"
                ? t(
                    "You are not authorized to edit tasks which are not assigned to you"
                  )
                : errorKeyFormate(error.response?.data.message + ""),
              errorConfig
            );
          },
        }
      );
    },
  });

  const updateCommentFormik = useFormik<
    z.infer<typeof createCommentTaskSchema>
  >({
    initialValues: {
      commentText: "",
      mantionEmail: [], // Initialize mantionEmail array for update form
    },
    validationSchema: toFormikValidationSchema(createCommentTaskSchema),
    onSubmit: (values) => {
      updateTaskCommentMutation.mutate(values, {
        onSuccess(data) {
          refetch();
          updateCommentFormik.resetForm();
          toast.success(data.data.message);
          setCommentId(null);
        },
        onError(error: any) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                  "You are not authorized to edit tasks which are not assigned to you"
                )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
        },
      });
    },
  });

  const handleEdit = (comment: Comment) => {
    setCommentId(comment.commentId);
    updateCommentFormik.setFieldValue("commentText", comment.commentText);
    // Populate mantionEmail when editing

    const initialMentions = resolveMentionsToEmails(
      comment.commentText
    );
    updateCommentFormik.setFieldValue("mantionEmail", initialMentions);
  };

  const handleBlur = () => {
    // Only submit if the text has actually changed to avoid unnecessary API calls
    const originalComment = task?.comments.find(
      (c) => c.commentId === commentId
    );
    if (
      originalComment?.commentText !== updateCommentFormik.values.commentText
    ) {
      updateCommentFormik.submitForm();
    } else {
      setCommentId(null); // Close edit mode if no change
    }
  };

  const [removeCommnet, setRemoveComment] = useState<Comment>();
  const handleRemove = (comment: Comment) => {
    removeTaskCommentMutation.mutate(comment.commentId, {
      onSuccess(data) {
        refetch();
        toast.success(data.data.message);
        setCommentId(null); // Exit edit mode if it was active
      },
      onError(error: any) {
        toast.error(
          error.response?.data.message == "Unauthorized"
            ? t(
                "You are not authorized to edit tasks which are not assigned to you"
              )
            : errorKeyFormate(error.response?.data.message + ""),
          errorConfig
        );
      },
    });
    setRemoveComment(undefined); // Close dialog
  };

  function isDateSevenDaysOld(inputDate: Date): boolean {
    const currentDate: Date = new Date();
    const sevenDaysAgo: Date = new Date(currentDate);
    sevenDaysAgo.setDate(currentDate.getDate() - 7);
    return new Date(inputDate) <= sevenDaysAgo;
  }

  const visibleComments = showAllShow
    ? props.task?.comments ?? []
    : (props.task?.comments ?? []).filter(
        (comment) => !isDateSevenDaysOld(new Date(comment.createdAt))
      );

  const allowed = (comment: Comment) => {
    return comment.commentByUserId === currantUser.user?.userId;
  };

  const scrollDemoRef = useRef<HTMLDivElement | null>(null);
  // Scrolls to bottom on initial load and when comments change
  useEffect(() => {
    if (scrollDemoRef.current) {
      scrollDemoRef.current.scrollTop = scrollDemoRef.current.scrollHeight;
    }
  }, [props.task?.comments]); // Dependency on comments ensures it scrolls when new comments load

  const allTask = useAllTaskQuery(props.projectId, false);
  const [isMentioning, setIsMentioning] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<AssignedUsers[]>();
  const projects = useProjectQuery();
  const resolveMentionsToEmails = (
    text: string,
  ): string[] => {
    const mentionRegex = /@([\w\s._%+-]+)/g;
    const mentionedEmails = new Set<string>();
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {

      const mentionStart = match.index;

      let bestMatchUser = null;
      let bestMatchLength = 0;

      if (projectDetailQuery.data?.data.data.assignedUsers) {
        for (const assignedUser of projectDetailQuery.data.data.data
          .assignedUsers) {
          const fullName = `${assignedUser.user.firstName ?? ""} ${
            assignedUser.user.lastName ?? ""
          }`.trim();
          const email = assignedUser.user.email || "";

          const possibleMentions = [fullName, email];

          for (const possible of possibleMentions) {
            if (!possible) continue;

            // Check if text starting after @ matches this possible user
            const substr = text.substring(
              mentionStart + 1,
              mentionStart + 1 + possible.length
            );

            if (
              substr.toLowerCase() === possible.toLowerCase() &&
              possible.length > bestMatchLength
            ) {
              bestMatchUser = assignedUser;
              bestMatchLength = possible.length;
            }
          }
        }
      }

      if (bestMatchUser) {
        // Push the highlight
        mentionedEmails.add(bestMatchUser.user.email)
      }
    }
    console.log( Array.from(mentionedEmails));
    
    return Array.from(mentionedEmails);
  };

  const getMentionQuery = (
    text: string,
    cursorPosition: number
  ): string | null => {
    const substring = text.slice(0, cursorPosition);
    const mentionRegex = /@([\w\s._%+-]*)$/;
    const match = substring.match(mentionRegex);
    return match ? match[1] : null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const assignedUsers =
      projectDetailQuery.data?.data?.data?.assignedUsers || [];

    commentFormik.setFieldValue("commentText", value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    const query = getMentionQuery(value, cursorPosition);

    if (query !== null) {
      setIsMentioning(true);

      const matches = assignedUsers.filter((u) => {
        const fullName = `${u.user.firstName ?? ""} ${u.user.lastName ?? ""}`
          .trim()
          .toLowerCase();
        const email = u.user.email.toLowerCase();
        return (
          fullName.startsWith(query.toLowerCase()) ||
          email.startsWith(query.toLowerCase())
        );
      });

      setFilteredUsers(matches);
    } else {
      setIsMentioning(false);
      setFilteredUsers([]);
    }

    const resolvedEmails = resolveMentionsToEmails(value);
    commentFormik.setFieldValue("mantionEmail", resolvedEmails);
  };

  const handleChangeUpdate = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const assignedUsers =
      projectDetailQuery.data?.data?.data?.assignedUsers || [];

    updateCommentFormik.setFieldValue("commentText", value);

    if (textareaRefUpdate.current) {
      textareaRefUpdate.current.style.height = "auto";
      textareaRefUpdate.current.style.height = `${textareaRefUpdate.current.scrollHeight}px`;
    }

    const query = getMentionQuery(value, cursorPosition);

    if (query !== null) {
      setIsMentioning(true);

      const matches = assignedUsers.filter((u) => {
        const fullName = `${u.user.firstName ?? ""} ${u.user.lastName ?? ""}`
          .trim()
          .toLowerCase();
        const email = u.user.email.toLowerCase();
        return (
          fullName.startsWith(query.toLowerCase()) ||
          email.startsWith(query.toLowerCase())
        );
      });

      setFilteredUsers(matches);
    } else {
      setIsMentioning(false);
      setFilteredUsers([]);
    }

    const resolvedEmails = resolveMentionsToEmails(value);
    updateCommentFormik.setFieldValue("mantionEmail", resolvedEmails);
  };

  const handleSelectUser = (user: AssignedUsers, update = false) => {
    const firstName = user.user.firstName ?? "";
    const lastName = user.user.lastName ?? "";
    const fullName = `${firstName} ${lastName}`.trim();
    const email = user.user.email;
    const mentionDisplay = fullName || email; // Prefer full name, fall back to email if name is empty

    const formik = update ? updateCommentFormik : commentFormik;
    const textareaRefCurrent = update
      ? textareaRefUpdate.current
      : textareaRef.current;

    const currentText = formik.values.commentText;
    const cursorPosition =
      textareaRefCurrent?.selectionStart || currentText.length;

    const textBeforeCursor = currentText.substring(0, cursorPosition);
    const lastAt = textBeforeCursor.lastIndexOf("@");

    if (lastAt === -1) {
      setIsMentioning(false);
      setFilteredUsers([]);
      return;
    }

    // Determine the query text that needs to be replaced
    const textAfterAt = currentText.substring(lastAt + 1);
    const queryEndIndex = textAfterAt.indexOf(" "); // Find the first space after '@'
    const queryText =
      queryEndIndex !== -1
        ? textAfterAt.substring(0, queryEndIndex)
        : textAfterAt;

    // Construct the new text by replacing '@query' with '@mentionDisplay '
    const newText =
      currentText.substring(0, lastAt) + // Part before the '@'
      "@" +
      mentionDisplay +
      " " + // The new mention + a space
      currentText.substring(lastAt + 1 + queryText.length); // Part after the original query

    formik.setFieldValue("commentText", newText);

    // Manually set cursor position after insertion
    const newCursorPosition = lastAt + 1 + mentionDisplay.length + 1; // After "@<name> "
    setTimeout(() => {
      if (textareaRefCurrent) {
        textareaRefCurrent.selectionStart = newCursorPosition;
        textareaRefCurrent.selectionEnd = newCursorPosition;
      }
    }, 0);


    const resolvedEmails = resolveMentionsToEmails(newText);
    formik.setFieldValue("mantionEmail", resolvedEmails);

    // Reset mention state
    setIsMentioning(false);
    setFilteredUsers([]);
  };

  const renderHighlightedText = (text: string) => {
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    const mentionRegex = /@(\S+)/g; // Match @something without space

    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const preText = text.substring(lastIndex, match.index);
      if (preText) parts.push(preText);

      const mentionStart = match.index;
      const mentionEnd = mentionRegex.lastIndex;

      let bestMatchUser = null;
      let bestMatchLength = 0;
      let matchingText = "";

      if (projectDetailQuery.data?.data.data.assignedUsers) {
        for (const assignedUser of projectDetailQuery.data.data.data
          .assignedUsers) {
          const fullName = `${assignedUser.user.firstName ?? ""} ${
            assignedUser.user.lastName ?? ""
          }`.trim();
          const email = assignedUser.user.email || "";

          const possibleMentions = [fullName, email];

          for (const possible of possibleMentions) {
            if (!possible) continue;

            // Check if text starting after @ matches this possible user
            const substr = text.substring(
              mentionStart + 1,
              mentionStart + 1 + possible.length
            );

            if (
              substr.toLowerCase() === possible.toLowerCase() &&
              possible.length > bestMatchLength
            ) {
              bestMatchUser = assignedUser;
              bestMatchLength = possible.length;
              matchingText = substr;
            }
          }
        }
      }

      if (bestMatchUser) {
        // Push the highlight
        parts.push(
          <span
            key={`${match.index}-highlight`}
            className="text-primary-700 font-semibold"
          >
            @{matchingText}
          </span>
        );
        lastIndex = mentionStart + 1 + bestMatchLength; // Move after the matched text
      } else {
        // No matching user found, treat as normal
        parts.push(text.substring(mentionStart, mentionEnd));
        lastIndex = mentionEnd;
      }
    }

    // Push the remaining text
    const remainingText = text.substring(lastIndex);
    if (remainingText) parts.push(remainingText);

    return parts;
  };

  const commentRender = (comment: Comments) => {
    return (
      <div className="flex gap-2 items-start overflow-visible">
        <div>
          <UserAvatar user={comment.commentByUser}></UserAvatar>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center gap-2">
            <div
              className={`text-sm font-semibold text-gray-450 ${
                comment.commentByUser.deletedAt
                  ? "!line-through !decoration-pink-500"
                  : ""
              }`}
            >
              {comment.commentByUser.firstName && comment.commentByUser.lastName
                ? comment.commentByUser.firstName +
                  " " +
                  comment.commentByUser.lastName
                : comment.commentByUser.email.split("_")[0]}
            </div>
            <div className="text-xs text-gray-450">
              {calculateTimeDifference(new Date(comment.createdAt))}{" "}
              {comment.commentByUser.deletedAt ? " (Deleted_User)" : ""}
            </div>
          </div>

          {commentId && commentId == comment.commentId ? (
            <div className="relative">
              <textarea
                ref={textareaRefUpdate}
                placeholder="Write a comment"
                className="mt-0 w-full p-1 h-fit border border-gray-300 rounded-md text-sm"
                name="commentText"
                onChange={handleChangeUpdate}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    // Allow Shift+Enter for new line
                    e.preventDefault(); // Prevent default new line
                    handleBlur(); // Submit
                  }
                }}
                autoFocus
                value={updateCommentFormik.values.commentText}
              ></textarea>
              {isMentioning &&
                filteredUsers &&
                filteredUsers?.length > 0 &&
                commentId &&
                commentId == comment.commentId && (
                  <div className="absolute z-10 bg-white border rounded shadow-md w-full max-h-40 overflow-auto top-full mt-1">
                    {filteredUsers?.map((user) => (
                      <div
                        key={user.user.userId}
                        className="p-2 hover:bg-gray-200 cursor-pointer"
                        onClick={() => handleSelectUser(user, true)}
                      >
                        {user.user.firstName}{" "}
                        {user.user.lastName || user.user.email}
                      </div>
                    ))}
                  </div>
                )}
              {updateCommentFormik.errors.commentText && (
                <ErrorMessage>
                  {t(updateCommentFormik.errors.commentText)}
                </ErrorMessage>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-450 font-norma">
              {renderHighlightedText(comment.commentText)}
              {comment.createdAt !== comment.updatedAt && (
                <span>
                   ({t("edited")}
                  {calculateTimeDifference(new Date(comment.updatedAt))})
                </span>
              )}
            </div>
          )}
          <ul className="inline-flex gap-7 ml-6 list-[circle] justify-start w-3/5">
            {allowed(comment) && (
              <>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    onClick={() => {
                      handleEdit(comment);
                    }}
                    className="p-0 font-normal h-fit"
                  >
                    <span className="relative -left-2 text-primary-500 text-xs">
                      {t("edit")}
                    </span>
                  </Button>
                </li>
                <li className="list-none">
                  <Button
                    variant={"none"}
                    onClick={() => {
                      setRemoveComment(comment);
                    }}
                    className="p-0 font-normal h-fit"
                  >
                    <span className="relative -left-2 text-danger text-xs">
                      {t("remove")}
                    </span>
                  </Button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (textareaRef.current && commentFormik.values.commentText) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    } else if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [commentFormik.values.commentText]);

  useEffect(() => {
    if (textareaRefUpdate.current && updateCommentFormik.values.commentText) {
      textareaRefUpdate.current.style.height = "auto";
      textareaRefUpdate.current.style.height =
        textareaRefUpdate.current.scrollHeight + "px";
    } else if (textareaRefUpdate.current) {
      textareaRefUpdate.current.style.height = "auto";
    }
  }, [updateCommentFormik.values.commentText]);

  return (
    <>
      <div className="w-full mt-2 flex flex-col-reverse ">
        <div>
          <div className="flex gap-x-2 items-center">
            <UserAvatar user={currantUser.user} className="w-9"></UserAvatar>
            <div className="relative w-full">
              <textarea
                ref={textareaRef}
                placeholder="Write comment @user"
                className="mt-0 w-full border-gray-300 p-1 border rounded-md text-sm"
                name="commentText"
                onChange={handleChange}
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    // Allow Shift+Enter for new line
                    e.preventDefault(); // Prevent default new line
                    commentFormik.handleSubmit(); // Submit
                  }
                }}
                value={commentFormik.values.commentText}
              ></textarea>
              {isMentioning &&
                filteredUsers &&
                filteredUsers?.length > 0 &&
                !commentId && ( // Only show suggestions for new comments, not when editing
                  <div className={`absolute z-10 bg-white border rounded shadow-lg w-full max-h-40 overflow-auto ${visibleComments.length > 3? "bottom-full":"top-full"} mb-1`}>
                    {filteredUsers?.map((user) => (
                      <div
                        key={user.user.userId}
                        className="p-2 hover:bg-gray-200 cursor-pointer"
                        onClick={() => handleSelectUser(user)}
                      >
                        {user.user.firstName}{" "}
                        {user.user.lastName || user.user.email}
                      </div>
                    ))}
                  </div>
                )}
              {commentFormik.values.commentText && (
                <Button
                  variant={"none"}
                  size={"icon"}
                  className="absolute bottom-1 right-1 mt-[1px] p-0 "
                  onClick={commentFormik.submitForm}
                >
                  <img
                    src={Send}
                    className=" brightness-50 grayscale opacity-60"
                  />
                </Button>
              )}
            </div>
          </div>
          {commentFormik.errors.commentText && (
            <ErrorMessage>{t(commentFormik.errors.commentText)}</ErrorMessage>
          )}
        </div>

        <div
          className="mb-2 flex flex-col gap-2 max-h-64 overflow-auto"
          ref={scrollDemoRef}
        >
          {/* Ensure comments are displayed chronologically, assuming backend provides in created order */}
          {visibleComments.map((comment) => {
            return commentRender(comment);
          })}
          {props.task?.comments?.length !== visibleComments?.length &&
            props.task?.comments?.length && (
              <div>
                <hr className="h-px my-3 bg-gray-200 border-0 dark:bg-gray-700"></hr>
                <div
                  className="text-center cursor-pointer"
                  onClick={() => setShowAllShow((prev) => !prev)}
                >
                  {!showAllShow ? t("Show all") : t("Less")}
                </div>
              </div>
            )}
        </div>
      </div>
      <Dialog
        isOpen={Boolean(removeCommnet)}
        onClose={() => setRemoveComment(undefined)} // Allow closing dialog
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCan} className="w-12 m-auto" />{" "}
          {t("common_delete_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              onClick={() => setRemoveComment(undefined)}
              isLoading={removeTaskCommentMutation.isPending}
              disabled={removeTaskCommentMutation.isPending}
            >
              {t("cancel")}
            </Button>
            <Button
              variant={"primary"}
              onClick={() => {
                removeCommnet ? handleRemove(removeCommnet) : "";
              }}
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default TaskComment;
